import { useEffect, useState } from 'react';
import type { DefaultAppOutcome, FileSource } from '../filesource/types';

const RESPONDED_KEY = 'markroom.defaultPrompt.responded';

function hasResponded(): boolean {
  try {
    return localStorage.getItem(RESPONDED_KEY) === 'true';
  } catch {
    return false;
  }
}

function markResponded(): void {
  try {
    localStorage.setItem(RESPONDED_KEY, 'true');
  } catch {
    // ignore
  }
}

/**
 * First-run prompt offering to make MarkRoom the default `.md` handler. Shown at
 * most once per platform (persisted), and re-invocable via `forceOpen`. macOS
 * sets it programmatically; Windows deep-links to Settings; if the macOS bridge
 * reports unsupported, manual guidance is shown (never a fake success).
 */
export function DefaultAppPrompt({
  source,
  forceOpen,
  onClose,
}: {
  source: FileSource;
  forceOpen: boolean;
  onClose: () => void;
}) {
  const eligible =
    source.platform === 'macos' || source.platform === 'windows' || source.platform === 'android';
  const [open, setOpen] = useState(() => eligible && (forceOpen || !hasResponded()));
  const [outcome, setOutcome] = useState<DefaultAppOutcome | null>(null);
  const [busy, setBusy] = useState(false);

  // Re-opening from Settings (⚙) forces the prompt visible again.
  useEffect(() => {
    if (forceOpen) setOpen(true);
  }, [forceOpen]);

  if (!eligible || !open) return null;

  const finish = () => {
    markResponded();
    setOpen(false);
    setOutcome(null);
    onClose();
  };

  // Apply the request and SHOW the result screen (guidance/success/manual
  // fallback). We mark responded so it won't auto-appear next launch, but keep
  // the dialog open until the user dismisses it via "Done".
  const accept = () => {
    setBusy(true);
    source.becomeDefaultApp().then(
      (result) => {
        setOutcome(result);
        setBusy(false);
        markResponded();
      },
      () => {
        setOutcome('unsupported');
        setBusy(false);
        markResponded();
      },
    );
  };

  const isWindows = source.platform === 'windows';
  const isAndroid = source.platform === 'android';
  const introText = isWindows
    ? 'Windows requires you to choose the default in Settings. We can open the Default apps page for you.'
    : isAndroid
      ? 'Android lets you choose a default when opening a file. We can show you how.'
      : '.md files will open in MarkRoom when you double-click them.';
  const actionLabel = isWindows ? 'Open Settings' : isAndroid ? 'Show me how' : 'Make default';
  const guidanceText = isWindows
    ? 'In Settings → Apps → Default apps, search for “MarkRoom” and set it for .md files. If Settings didn’t open, launch it from the Start menu.'
    : 'Open a .md file from your file manager, choose MarkRoom, then tap “Always”.';
  // Only reachable on macOS/Windows if the request fails (Android resolves to
  // 'guidance'); the manual fallback below is the macOS Finder route.
  const unsupportedText =
    'In Finder, select a .md file → File → Get Info → “Open with” → MarkRoom → “Change All…”.';

  return (
    <div className="absolute inset-x-0 top-0 z-20 flex justify-center p-3">
      <div
        role="dialog"
        aria-label="Make MarkRoom the default Markdown app"
        className="mr-prompt w-full max-w-xl rounded-lg border border-neutral-300 bg-white p-4 shadow-xl dark:border-neutral-700 dark:bg-neutral-800"
      >
        {outcome === null ? (
          <>
            <p className="font-medium text-neutral-800 dark:text-neutral-100">
              Make MarkRoom your default Markdown app?
            </p>
            <p className="mt-1 text-sm text-neutral-500">{introText}</p>
            <div className="mt-3 flex justify-end gap-2">
              <button type="button" className="mr-btn" onClick={finish} disabled={busy}>
                Not now
              </button>
              <button
                type="button"
                className="mr-btn mr-btn--primary"
                onClick={accept}
                disabled={busy}
              >
                {actionLabel}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="font-medium text-neutral-800 dark:text-neutral-100">
              {outcome === 'set'
                ? 'MarkRoom is now your default Markdown app.'
                : outcome === 'guidance'
                  ? guidanceText
                  : 'Set MarkRoom manually'}
            </p>
            {outcome === 'unsupported' && (
              <p className="mt-1 text-sm text-neutral-500">{unsupportedText}</p>
            )}
            <div className="mt-3 flex justify-end">
              <button type="button" className="mr-btn mr-btn--primary" onClick={finish}>
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
